import { Controller } from "@hotwired/stimulus"

// Site-wide viewing history and homepage frequency are scoped to each popup.
const fallbackStorage = new Map()

export default class extends Controller {
  static values = { id: Number, home: Boolean, daily: Boolean, start: String, end: String, timeZone: String }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.afterLoad = () => {
      this.openTimer = window.setTimeout(() => this.showPopup(), 500)
    }
    if (document.readyState === "complete") {
      this.afterLoad()
    } else {
      window.addEventListener("load", this.afterLoad, { once: true })
    }
  }

  showPopup() {
    if (!this.element.isConnected || document.documentElement.hasAttribute("data-turbo-preview")) return

    const day = new Intl.DateTimeFormat("en-CA", {
      timeZone: this.timeZoneValue, year: "numeric", month: "2-digit", day: "2-digit"
    }).formatToParts(new Date())
    const part = (type) => day.find((p) => p.type === type).value
    const today = `${part("year")}-${part("month")}-${part("day")}`
    if (today < this.startValue || today > this.endValue) return

    const homeKey = `popups:${this.idValue}:home`
    const seenKey = `popups:${this.idValue}:seen`
    if (this.homeValue) {
      if (this.dailyValue && this.read(homeKey) === today) return
    } else if (this.hasSeenPopup()) {
      return
    }

    this.element.classList.remove("is-closing")
    this.previousFocus = document.activeElement
    this.element.showModal()
    this.write(seenKey, "seen")
    if (this.homeValue) this.write(homeKey, today)
  }

  disconnect() {
    this.closeImmediately()
  }

  close(event) {
    // Prevent Escape from closing the native dialog before the fade finishes.
    event?.preventDefault()
    if (!this.element.open || this.closing) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.closeImmediately()
      return
    }

    this.closing = true
    this.element.classList.add("is-closing")
    // Also finish if animationend is interrupted or animations are disabled.
    this.closeTimer = window.setTimeout(() => this.closeImmediately(), 250)
  }

  animationEnded(event) {
    if (event.target === this.element && event.animationName === "scheduled-popup-fade-out" && this.closing) {
      this.closeImmediately()
    }
  }

  closeImmediately() {
    window.removeEventListener("load", this.afterLoad)
    window.clearTimeout(this.openTimer)
    window.clearTimeout(this.closeTimer)
    this.closing = false
    this.element.classList.remove("is-closing")
    if (!this.element.open) return
    this.element.close()
    if (this.previousFocus?.isConnected) this.previousFocus.focus()
  }

  backdrop(event) {
    if (event.target !== this.element) return
    const bounds = this.element.getBoundingClientRect()
    if (event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom) this.close()
  }

  hasSeenPopup() {
    return Boolean(this.read(`popups:${this.idValue}:seen`))
  }

  read(key) {
    try {
      const prefix = `${encodeURIComponent(key)}=`
      const cookie = document.cookie.split(";").map((part) => part.trim())
        .find((part) => part.startsWith(prefix))
      return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : fallbackStorage.get(key)
    } catch { return fallbackStorage.get(key) }
  }

  write(key, value) {
    // Keep campaign history through its final day in every supported time zone.
    const expires = new Date(`${this.endValue}T00:00:00Z`)
    expires.setUTCDate(expires.getUTCDate() + 2)
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    try {
      document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; Path=/; Expires=${expires.toUTCString()}; SameSite=Lax${secure}`
      fallbackStorage.delete(key)
      if (this.read(key) === value) return
    } catch { /* Cookies may be disabled. */ }
    fallbackStorage.set(key, value)
  }
}
